/*
 * QUEST SOFTWARE PROPRIETARY INFORMATION
 *
 * This software is confidential.  Quest Software Inc., or one of its
 * subsidiaries, has supplied this software to you under terms of a
 * license agreement, nondisclosure agreement or both.
 *
 * You may not copy, disclose, or use this software except in accordance with
 * those terms.
 *
 *
 * Copyright 2026 Quest Software Inc.
 * ALL RIGHTS RESERVED.
 *
 * QUEST SOFTWARE INC. MAKES NO REPRESENTATIONS OR
 * WARRANTIES ABOUT THE SUITABILITY OF THE SOFTWARE,
 * EITHER EXPRESS OR IMPLIED, INCLUDING BUT NOT LIMITED
 * TO THE IMPLIED WARRANTIES OF MERCHANTABILITY,
 * FITNESS FOR A PARTICULAR PURPOSE, OR
 * NON-INFRINGEMENT.  QUEST SOFTWARE SHALL NOT BE
 * LIABLE FOR ANY DAMAGES SUFFERED BY LICENSEE
 * AS A RESULT OF USING, MODIFYING OR DISTRIBUTING
 * THIS SOFTWARE OR ITS DERIVATIVES.
 */

import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, Subject } from 'rxjs';
import { SidebarHttpService } from './sidebar-http.service';
import { WcfLinkToAuiLinkMapping } from '../models/nav-item';
import { skipEmpty } from '../../../shared/utils/skip-empty.operator';
import { LeftNavigationMenu, NavigationMenuItem } from '../models/navigation-menu-item';
import { shareReplay } from 'rxjs/operators';

@Injectable({
  providedIn: 'root',
})
export class SidebarService {
  private sidebarItems$: Observable<LeftNavigationMenu> | undefined;
  private foldedState$$ = new BehaviorSubject<boolean>(true);
  private editMode$$ = new BehaviorSubject<boolean>(false);
  private isHover$$ = new BehaviorSubject<boolean>(false);
  private navItems$$ = new BehaviorSubject<NavigationMenuItem[] | undefined>(undefined);
  private bookmarksItem$$ = new BehaviorSubject<NavigationMenuItem | undefined>(undefined);
  private configItems$$ = new BehaviorSubject<NavigationMenuItem[] | undefined>(undefined);
  private menuItems$$ = new BehaviorSubject<NavigationMenuItem | undefined>(undefined);

  foldedState$ = this.foldedState$$.asObservable().pipe(skipEmpty());
  editMode$ = this.editMode$$.asObservable().pipe(skipEmpty());
  isHover$ = this.isHover$$.asObservable().pipe(skipEmpty());
  navItems$ = this.navItems$$.asObservable().pipe(skipEmpty());
  bookmarksItem$ = this.bookmarksItem$$.asObservable().pipe(skipEmpty());
  menuItems$ = this.menuItems$$.asObservable().pipe(skipEmpty());
  configItems$ = this.configItems$$.asObservable().pipe(skipEmpty());

  currentSelectedId = new Subject<string>();
  redirectLink$ = new BehaviorSubject<string | null>(null);
  deletedBookmark = new Subject<string>();
  tabsSelectIndex$ = new BehaviorSubject(0);
  sidebarWidth$ = new BehaviorSubject('240px');
  selectedNavItem = new Subject<boolean>();
  pinnedState$ = new BehaviorSubject<boolean>(false);
  bookmarkAdded = new Subject<boolean>();
  menuShouldBeFolded$ = new BehaviorSubject(true);
  homePageNavItem = new Subject<NavigationMenuItem>();

  _configurationItems: NavigationMenuItem[] = [];
  /**
   * Constructor
   */
  constructor(private sidebarHttpService: SidebarHttpService) {
    const sidebarWidth = localStorage.getItem('sidebarWidth')
    this.sidebarWidth$.next(
      sidebarWidth ? sidebarWidth : '240px'
    );
  }

  updateMenuItems(newData: NavigationMenuItem) {
    this.menuItems$$.next(newData);
  }

  updateNavItems(items: NavigationMenuItem[]) {
    this.navItems$$.next(items);
  }

  updateBooksmarksItem(item: NavigationMenuItem) {
    this.bookmarksItem$$.next(item);
  }

  updateConfigItems(items: NavigationMenuItem[]) {
    this.configItems$$.next(items);
  }

  updateHoverState(state: boolean) {
    this.isHover$$.next(state);
  }

  get configurationItems(): NavigationMenuItem[] {
    return this._configurationItems;
  }

  set configurationItems(items: NavigationMenuItem[]) {
    this._configurationItems = items;
  }

  getSidebarItems(): Observable<LeftNavigationMenu> {
    if (!this.sidebarItems$) {
      this.sidebarItems$ = this.sidebarHttpService
        .getLeftSideMenu()
        .pipe(shareReplay(1));
    }

    return this.sidebarItems$;
  }

  clearSidebarItemsCache() {
    this.sidebarItems$ = undefined;
  }

  disableMenus(menuIds: string[]) {
    this.clearSidebarItemsCache();
    return this.sidebarHttpService.disableMenus(menuIds);
  }

  changeSelected(selectedId: string) {
    this.currentSelectedId.next(selectedId);
  }

  setFoldedState(state: boolean) {
    this.foldedState$$.next(state);
  }

  setEditState(state: boolean) {
    this.editMode$$.next(state);
  }

  changeHomePageNavItem(item: NavigationMenuItem) {
    this.clearSidebarItemsCache();
    this.homePageNavItem.next(item);
  }

  replaceItemsLinkAsPerAUIMapping(items: NavigationMenuItem[]) {
    try {
      items.forEach((item) => {
        if (item.link) {
          this.replaceLinkAsPerAUIMapping(item);
        }
        if (item.children && item.children.length > 0) {
          this.replaceItemsLinkAsPerAUIMapping(item.children);
        }
      });
    } catch (err) {
      console.error(err);
    }
  }

  replaceLinkAsPerAUIMapping(item: NavigationMenuItem) {
    try {
      if (item.link?.includes('wcf')) {
        let viewId = item.link.split('viewId=');
        let auiLink = WcfLinkToAuiLinkMapping[viewId[1]] || '';
        if (auiLink !== '') {
          item.link = WcfLinkToAuiLinkMapping[viewId[1]];
        }
      }
    } catch (err) {
      console.error(err);
    }
  }

  findNavItemBylink(items: NavigationMenuItem[], link: string): NavigationMenuItem | undefined {
    try {
      for (const menuItem of items) {
        if (menuItem.link === link) {
          return menuItem;
        }
        if (menuItem.children && menuItem.children.length > 0) {
          const found = this.findNavItemBylink(menuItem.children, link);
          if (found) {
            return found;
          }
        }
      }
    } catch (err) {
      console.error(err);
    }
  }

  findNavItemById(menuItems: NavigationMenuItem[], id: string): NavigationMenuItem | undefined {
    try {
      for (const menuItem of menuItems) {
        if (menuItem.id === id) {
          return menuItem;
        }
        if (menuItem.children && menuItem.children.length > 0) {
          const found = this.findNavItemById(menuItem.children, id);
          if (found) {
            return found;
          }
        }
      }
    } catch (err) {
      console.error(err);
    }
  }
}
